import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { authService } from '../services/auth';
import { twoFactor } from '../services/twofa';
import { RegisterPayload, User } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  // Returns { mfaRequired: true, mfaToken } when the account has 2FA on — the
  // caller then collects a code and calls completeLogin2fa. Otherwise signs in.
  login: (email: string, password: string) => Promise<{ mfaRequired: boolean; mfaToken?: string }>;
  completeLogin2fa: (mfaToken: string, code: string) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<{ verificationRequired: boolean }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('access_token');
    if (token) {
      // Verify token and get user info
      api
        .get('/api/user/info')
        .then((response) => {
          setUser({
            user_id: response.data.user_id,
            email: response.data.email,
            name: response.data.name ?? null,
            phone: response.data.phone ?? null,
            created_at: response.data.created_at,
          });
        })
        .catch(() => {
          localStorage.removeItem('access_token');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authService.login(email, password);
    // 2FA enabled: no token yet — hand the caller the mfa_token to finish with a code.
    if (response.mfa_required && response.mfa_token) {
      return { mfaRequired: true, mfaToken: response.mfa_token };
    }
    if (response.access_token) {
      localStorage.setItem('access_token', response.access_token);
    }
    setUser({
      user_id: response.user_id ?? '',
      email: response.email,
    });
    return { mfaRequired: false };
  };

  const completeLogin2fa = async (mfaToken: string, code: string) => {
    const data = await twoFactor.verifyLogin(mfaToken, code);
    localStorage.setItem('access_token', data.access_token);
    setUser({ user_id: data.user_id ?? '', email: data.email });
  };

  const register = async (payload: RegisterPayload): Promise<{ verificationRequired: boolean }> => {
    const response = await authService.register(payload);
    // When verification is required, the server creates the account but does NOT
    // return a token — the user must click the email link before they can sign in.
    if (response.verification_required || !response.access_token) {
      return { verificationRequired: true };
    }
    localStorage.setItem('access_token', response.access_token);
    setUser({
      user_id: response.user_id ?? '',
      email: response.email,
      name: payload.name ?? null,
      phone: payload.phone ?? null,
    });
    return { verificationRequired: false };
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, completeLogin2fa, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
